import React, { useEffect, useState, useMemo } from 'react';
import { ButtonOption, FieldType, Option } from '../../types/mold';
import MultiSelector from './MultiSelector';
import { JsonComputedConfig } from '../../utils/jsonComputedEngine';
import NewSearchDropdown from './NewSearchDropdown';

export interface FormFieldConfig {
  type: FieldType;
  placeholder?: string;
  options?: Option[];
  fetch?: () => Promise<Option[]>;
  noResultsMessage?: string;
  renderItem?: (item: SearchDropdownItem) => React.ReactNode;
  enforceDropdownSelection?: boolean;
  validationMessage?: string;
  onInvalidInput?: (invalidValue: string) => void;
  allowInvalidInput?: boolean;
  minDate?: Date; // Minimum allowed date for 'date' type
  maxDate?: Date; // Maximum allowed date for 'date' type
  onSaveCustomValue?: (value: string) => void; // Function to save custom value for select_with_other_and_custom
  // MultiSelector "other" option support
  allowOther?: boolean; // Enable "other" option functionality for multi_selector
  otherOptionValue?: string; // Value to identify "other" option
  customValuePlaceholder?: string; // Placeholder for custom input field
}

interface FormFieldProps {
  formType?: FieldType;
  value: any;
  onChange: (value: any) => void;
  options?: Option[];
  fetch?: () => Promise<Option[]>;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  label?: string;
  required?: boolean;
  testIdIdentifier: string;
  hasError?: boolean;
  isLoading?: boolean;
  error?: string;
  onFilterChange?: (searchTerm: string) => void;
  noResultsMessage?: string;
  enforceDropdownSelection?: boolean;
  validationMessage?: string;
  onInvalidInput?: (value: string) => void;
  allowInvalidInput?: boolean;
  previewMode?: boolean;
  disabled?: boolean;
  toggleLabel?: {
    on: string;
    off: string;
  }
  onChangeInputValidity?: (isValid: boolean) => void;
  minDate?: Date;
  maxDate?: Date;
  onSaveCustomValue?: (value: string) => void; // Function to save custom value for select_with_other_and_custom
  id?: string; // Added for label/input association
  // New props for creating options
  allowCreateNewOption?: boolean;
  createOptionConfig?: {
    entityType: string;
    onCreateSuccess?: (newOption: Option) => void;
  };
  // Computed field specific props
  computedConfig?: {
    id: string;
    dependencyFields?: string[];
    jsonConfig?: JsonComputedConfig; // JSON-based computation configuration
  };
  dependencyValues?: Record<string, any>; // Values of dependency fields for computed fields
  // MultiSelector custom value support
  allowOther?: boolean; // Enable adding custom values for multi_selector
  customValuePlaceholder?: string; // Placeholder for custom input field
  otherOptionValue?: string; // Value to identify "other" option
}

interface SearchDropdownItem extends Option {
  icon?: React.ReactNode; // Ensure compatibility with ReactNode
  secondaryText?: string;
  imageUrl?: string;
}

// Utility function for rendering computed values with multi-line support
const renderComputedValue = (value: string | number | boolean): React.ReactNode => {
  if (typeof value === 'boolean') return value ? 'ใช่' : 'ไม่ใช่';
  if (typeof value === 'number') return value.toString();
  if (!value) return '-';

  const strValue = String(value);
  const lines = strValue.split('\n');
  if (lines.length === 1) {
    return <span>{strValue}</span>;
  }
  
  return (
    <div className="whitespace-pre-line">
      {lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </div>
  );
};

const FormField: React.FC<FormFieldProps> = ({
  formType = 'text',
  value,
  onChange,
  options: initialOptions,
  fetch,
  placeholder,
  className = '',
  autoFocus = false,
  label = '',
  required = false,
  testIdIdentifier,
  isLoading,
  error,
  onFilterChange,
  noResultsMessage = 'ไม่พบตัวเลือก',
  enforceDropdownSelection = false,
  validationMessage = 'Please select a valid option',
  onInvalidInput,
  allowInvalidInput = true,
  previewMode = false,
  disabled = false,
  hasError = false,
  toggleLabel = {
    on: 'ใช่',
    off: 'ไม่',
  },
  onChangeInputValidity,
  minDate,
  maxDate,
  onSaveCustomValue,
  id,
  allowCreateNewOption = false,
  createOptionConfig,
  allowOther = false,
  customValuePlaceholder = 'ระบุค่าที่ต้องการเพิ่ม',
  // computedConfig and dependencyValues are not needed since 
  // the parent computes the value and passes it via the value prop
}) => {
  // For computed fields, the value is already computed at the parent level
  // and passed via the value prop, so we don't need to recompute it here


  const baseInputClass = `
    w-full border rounded-lg py-2 px-3 text-gray-900 text-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300
    transition-shadow duration-200 h-full
  `;

  // Add a special class for date inputs to ensure better mobile/tablet display with calendar styling
  const dateInputClass = `
    min-h-[42px] touch-manipulation appearance-none`;
  
  // Add a special class for select inputs to make them more touch-friendly
  const selectInputClass = `
    h-[42px] min-h-[42px] touch-manipulation pr-10 
    bg-[url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E")] 
    bg-no-repeat bg-[right_0.5rem_center] bg-[length:1.5em_1.5em]
    focus:border-blue-500 focus:border-2`;

  const errorClass = hasError ? 'border-red-500 focus:ring-red-500' : '';
  const bgColor = disabled ? 'bg-gray-100' : 'bg-white border-gray-200';
  const combinedClassName = `${baseInputClass} ${className} ${bgColor} ${errorClass}`;
  // Specific combined class for date inputs
  const dateClassName = `${combinedClassName} ${dateInputClass}`;
  // Specific combined class for select inputs
  const selectClassName = `${combinedClassName} ${selectInputClass}`;

  const [options, setOptions] = useState<ButtonOption[]>(
    // Keep original value types (string | number) instead of forcing to string.
    (initialOptions || []).map(option => ({
      ...option,
    }))
  );
  const [searchTerm, setSearchTerm] = useState(() => {
    const selectedOption = initialOptions?.find(option => String(option.value) === String(value));
    return selectedOption ? selectedOption.label : '';
  });

  // Memoize expensive calculations
  const isNotInOptions = useMemo(() => 
    (value !== undefined && value !== null) && !options.some(option => String(option.value) === String(value)),
    [value, options]
  );
  
  const isOtherSelected = useMemo(() => 
    value !== '' && (value === 'other_custom_text' || ((value !== undefined && value !== null) && isNotInOptions)),
    [value, isNotInOptions]
  );
  
  // Initialize customText state with an appropriate value
  const [customText, setCustomText] = useState(() => {
    // If "other_custom_text" is explicitly selected, start with empty text
    if (value === 'other_custom_text') {
      return '';
    }
    // If the value isn't in options, use that value as custom text
    else if ((value !== undefined && value !== null && value !== '') && isNotInOptions) {
      return value;
    }
    // Otherwise, empty string
    return '';
  });

  // Update customText when value changes from outside the component
  useEffect(() => {
    if (formType === 'select_with_other_and_custom') {
      // If the value is not in options and not "other_custom_text", set it as customText
  if ((value !== undefined && value !== null && value !== '') && value !== 'other_custom_text' && !options.some(option => String(option.value) === String(value))) {
        setCustomText(value);
      }
      // If explicit options are selected, clear customText
  else if ((value !== undefined && value !== null && value !== '') && value !== 'other_custom_text' && options.some(option => String(option.value) === String(value))) {
        setCustomText('');
      }
    }
  }, [value, options, formType]);

  useEffect(() => {
    if (formType && [
      'select',
      'select_with_other',
      'select_with_other_and_custom',
      'search_dropdown',
      'multi_selector',
    ].includes(formType) && fetch) {
      const fetchOptions = async () => {
        const fetchedOptions = await fetch();
        setOptions(fetchedOptions.map(({ value, label, ...rest }) => ({ ...rest, value: value, label: label })));
      };
      fetchOptions();
    }
  }, [formType, fetch]);

  // Create a refresh function that can be called after creating new options
  const refreshOptions = React.useCallback(async () => {
    if (fetch) {
      const fetchedOptions = await fetch();
      setOptions(fetchedOptions.map(({ value, label, ...rest }) => ({ ...rest, value: value, label: label })));
    }
  }, [fetch]);

  useEffect(() => {
    if (value !== undefined && value !== null && value !== '') {
  const selectedOption = options.find(option => String(option.value) === String(value));
      if (selectedOption) {
        setSearchTerm(selectedOption.label);
      }
    }
  }, [value, options]);

  const renderPreviewMode = (displayValue: string) => {
    if (!displayValue) {
      return <div className="py-2 text-sm sm:text-lg font-bold">-</div>;
    }
    const strValue = typeof displayValue === 'string' ? displayValue : String(displayValue);
    const lines = strValue.split('\n');
    return (
      <div className="py-2 text-sm sm:text-lg font-bold whitespace-pre-line">
        {lines.map((line, idx) => (
          <div key={idx}>{line}</div>
        ))}
      </div>
    );
  };

  switch (formType) {
    case 'toggle':
      const displayValue = value ? toggleLabel.on : toggleLabel.off;
      if (previewMode) {
        return renderPreviewMode(displayValue);
      }
      return (
        <label className="inline-flex items-center cursor-pointer py-2">
          <input
            onClick={() => !disabled && onChange(!value)}
            type="checkbox"
            checked={value}
            className="sr-only peer"
          />
          <div className={`relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
          <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">{displayValue}</span>
        </label>
      );
    case 'select':
    case 'select_with_other':
      if (previewMode) {
        const selectedOption = options?.find(option => String(option.value) === String(value));
        return renderPreviewMode(selectedOption?.label || value);
      }
      return (
        <select
          value={value || ''}
          onChange={(e) => {
            const selected = e.target.value;
            const matched = options.find(option => String(option.value) === String(selected));
            if (matched) onChange(matched.value);
            else onChange(selected);
          }}
          className={selectClassName}
          required={required}
          data-cy={testIdIdentifier}
          disabled={disabled}
        >
          {label && <option value="">เลือก {label}</option>}
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {formType === 'select_with_other' && (
            <option value="other_custom_text">อื่นๆ</option>
          )}
        </select>
      );
    case 'select_with_other_and_custom':
      if (previewMode) {
        const selectedOption = options?.find(option => String(option.value) === String(value));
        return renderPreviewMode(selectedOption?.label || value);
      }

      // Handle saving custom value (string or number)
      const handleSaveCustomValue = () => {
        if (customText && customText.trim() !== '') {
          const trimmedText = customText.trim();
          // Try to parse as number if possible
          const parsedValue = !isNaN(Number(trimmedText)) && trimmedText !== '' ? Number(trimmedText) : trimmedText;
          // Check if value already exists in options (compare as string or number)
          const alreadyExists = options.some(option => String(option.value) === String(parsedValue));
          if (!alreadyExists && onSaveCustomValue) {
            onSaveCustomValue(parsedValue);
          }
          onChange(parsedValue);
        }
      };

      return (
        <div className='w-full flex flex-col gap-2 justify-start'>
          <select
            value={value !== undefined && value !== null ? (isOtherSelected ? 'other_custom_text' : String(value)) : ''}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue !== 'other_custom_text') {
                setCustomText('');
                // If the option is a number, convert it back
                const matchedOption = options.find(option => String(option.value) === newValue);
                if (matchedOption) {
                  onChange(matchedOption.value);
                } else {
                  onChange(newValue);
                }
              } else if (newValue === 'other_custom_text' && !isOtherSelected) {
                setCustomText('');
                onChange('other_custom_text');
              }
            }}
            className={selectClassName}
            required={required}
            data-cy={testIdIdentifier}
            disabled={disabled}
          >
            {label && <option value="">เลือก {label}</option>}
            {options?.map((option) => (
              <option key={option.value} value={String(option.value)}>
                {option.label}
              </option>
            ))}
            {options.filter(option => String(option.value) === 'other_custom_text').length === 0 && (
              <option value="other_custom_text">อื่นๆ</option>
            )}
          </select>
          {isOtherSelected && (
            <div className="flex">
              <input
                type="text"
                value={customText || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setCustomText(newValue);
                  if (newValue.trim() !== '') {
                    // Try to parse as number if possible
                    const parsedValue = !isNaN(Number(newValue)) && newValue !== '' ? Number(newValue) : newValue;
                    onChange(parsedValue);
                  }
                }}
                onBlur={() => {
                  if (customText && customText.trim() !== '') {
                    handleSaveCustomValue();
                  }
                }}
                className={`${combinedClassName} w-full`}
                autoFocus={autoFocus}
                required={required}
                data-cy={`custom-${testIdIdentifier}`}
                disabled={disabled}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveCustomValue();
                    e.currentTarget.blur();
                  }
                }}
                placeholder="ระบุค่าที่ต้องการเพิ่ม"
              />
            </div>
          )}
        </div>
      );

    case 'button_options':
      if (previewMode) {
        const selectedOption = options?.find(option => String(option.value) === String(value));
        return renderPreviewMode(selectedOption?.label || value);
      }
      return (
        <div className="flex flex-row gap-2">
          {options?.map((option) => {
            const isSelected = value === option.value;
            const borderColor = isSelected ? '' : (option.borderColor ?? 'border-gray-300');
            const textColor = isSelected ? 'text-white' : (option.textColor ?? 'text-gray-700');
            const hoverColor = isSelected ? '' : (option.hoverColor ?? 'hover:bg-gray-200');
            const bgColor = isSelected ? (option.selectedBgColor ?? 'bg-green-500') : (option.bgColor ?? 'bg-white');
            const buttonClassName = `
              shadow text-md w-full py-3 px-4 rounded-md flex items-center justify-center
              ${textColor}
              ${hoverColor}
              ${bgColor}
              border ${borderColor} 
              ${isSelected ? 'bg-blue-500' : 'bg-white-200 hover:text-white'}`;
            const Icon = option.icon ? option.icon : null;
            return (
              <button
                onClick={() => {
                  onChange(option.value)
                }}
                key={option.value}
                className={buttonClassName}
                data-cy={`${testIdIdentifier}-${option.value}`}
              >
                {option.icon && Icon ? <Icon className="mr-2" /> : <></>}
                {option.label}
              </button>
            )
          })}
        </div>
      )

    case 'date':
      if (previewMode) {
        // Always use DD/MM/YYYY format (en-GB locale) for consistency
        const dateValue = value ? new Date(value).toLocaleDateString('en-GB') : '';
        return renderPreviewMode(dateValue);
      }
      // Support minDate and maxDate
      const minDateStr = minDate instanceof Date ? minDate.toISOString().split('T')[0] : undefined;
      let maxDateStr = maxDate instanceof Date ? maxDate.toISOString().split('T')[0] : undefined;
        maxDateStr = maxDate ? maxDate.toISOString().split('T')[0] : undefined;
      return (
        <input
          type="date"
          value={value ? new Date(value).toISOString().split('T')[0] : ''}
          onChange={(e) => {
            const selectedDate = new Date(e.target.value);
            if (minDateStr && e.target.value < minDateStr) {
              if (onChangeInputValidity) onChangeInputValidity(false);
              return;
            }
            if (maxDateStr && e.target.value > maxDateStr) {
              if (onChangeInputValidity) onChangeInputValidity(false);
              return;
            }
            if (onChangeInputValidity) onChangeInputValidity(true);
            onChange(selectedDate);
          }}
          className={dateClassName}
          autoFocus={autoFocus}
          required={required}
          data-cy={testIdIdentifier}
          disabled={disabled}
          min={minDateStr}
          max={maxDateStr}
        />
      );

    case 'number':
      if (previewMode) {
        const formattedValue = value ? value.toLocaleString('en-US') : '';
        return renderPreviewMode(formattedValue);
      }
      return (
        <input
          id={id}
          type="number"
          value={value}
          onChange={(e) => {
            if (e.target.value || e.target.value !== '') {
              onChange(parseInt(e.target.value))
            } else {
              onChange('')
            }
          }}
          className={combinedClassName}
          placeholder={placeholder}
          autoFocus={autoFocus}
          required={required}
          data-cy={testIdIdentifier}
          min={0}
          disabled={disabled}
        />
      );

    case 'textarea':
      if (previewMode) {
        return renderPreviewMode(value || '');
      }
      return (
        <textarea
          id={id}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${combinedClassName} min-h-[80px]`}
          placeholder={placeholder}
          autoFocus={autoFocus}
          required={required}
          data-cy={testIdIdentifier}
          disabled={disabled}
        />
      );

    case 'search_dropdown':
      if (previewMode) {
        const selectedOption = options.find(option => String(option.value) === String(value));
        return renderPreviewMode(selectedOption?.label || value);
      }
      // return <SearchDropdown
      //   value={value}
      //   onChange={onChange}
      //   searchTerm={searchTerm}
      //   setSearchTerm={setSearchTerm}
      //   options={options}
      //   placeholder={placeholder}
      //   className={className}
      //   autoFocus={autoFocus}
      //   required={required}
      //   testIdIdentifier={testIdIdentifier}
      //   isLoading={isLoading}
      //   error={error}
      //   onFilterChange={onFilterChange}
      //   noResultsMessage={noResultsMessage}
      //   enforceDropdownSelection={enforceDropdownSelection}
      //   validationMessage={validationMessage}
      //   onInvalidInput={onInvalidInput}
      //   allowInvalidInput={allowInvalidInput}
      //   disabled={disabled}
      //   hasError={hasError}
      //   onChangeInputValidity={onChangeInputValidity}
      // />
      return <NewSearchDropdown
        value={value}
        onChange={onChange}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        options={options}
        placeholder={placeholder}
        testIdIdentifier={testIdIdentifier}
        isLoading={isLoading}
        onFilterChange={onFilterChange}
        disabled={disabled}
        hasError={hasError}
        allowCreateNewOption={allowCreateNewOption}
        createOptionConfig={createOptionConfig}
        onRefreshOptions={refreshOptions}
      />
    case 'multi_selector':
      return (
        <MultiSelector
          options={options}
          label={label}
          labelForNoneSelected={noResultsMessage}
          onSelect={onChange}
          value={Array.isArray(value) ? value : []}
          hideLabel={true}
          data-cy={testIdIdentifier}
          idScope={testIdIdentifier}
          previewMode={previewMode}
          displayMode='modal'
          allowOther={allowOther}
          onSaveCustomValue={onSaveCustomValue}
          customValuePlaceholder={customValuePlaceholder}
        />
      );
    case 'computed':
      if (previewMode) {
        return renderPreviewMode(value || '');
      }
      
      return (
        <div className={`py-2 px-3 bg-gray-50 border rounded-lg text-gray-700 ${className}`}>
          <div className="text-sm text-gray-600 font-medium">
            {renderComputedValue(value)}
          </div>
        </div>
      );
    case 'text':
    default:
      if (previewMode) {
        return renderPreviewMode(value || '');
      }
      return (
        <input
          id={id}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={combinedClassName}
          placeholder={placeholder}
          autoFocus={autoFocus}
          required={required}
          data-cy={testIdIdentifier}
          disabled={disabled}
        />
      );
  }
};

export default FormField;
